import { useTranslation } from "react-i18next"
import { To, useNavigate } from "react-router"
import { DotsThreeVertical } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button.tsx"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"
import { toast } from "@/hooks/useToast"
import { joinStrings } from "@/lib/joinString"
import { ManageDuplicatesDrawer } from "@/modules/patients/manage-duplicates/manage-duplicates-drawer"
import { PatientSearchItem } from "@/modules/patients/patient-list/types/PatientSearchItem"
import { useMarkPatientAsPotentialDuplicateMutation } from "@/modules/patients/services/useMarkPatientAsPotentialDuplicateMutation"
import { useRejectPatientAsPotentialDuplicateMutation } from "@/modules/patients/services/useRejectPatientAsPotentialDuplicateMutation"
import useCallContextStore from "@/stores/useCallContextStore"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface PatientTableActionMenuProps {
  data: PatientSearchItem
  openPatientInboundCall: (patientId: string, patientName: string) => void
  onPatientUpdated: () => void
}

export type PatientNameNavigationState = {
  patientName?: string
}

const PatientTableActionMenu = (props: PatientTableActionMenuProps) => {
  const { data, openPatientInboundCall, onPatientUpdated } = props
  const { country, patientId, firstName, lastName, isPotentialDuplicate } = data
  const callId = useCallContextStore((state) => state.callID)
  const { t } = useTranslation()
  const user = useUser()
  const canManageDuplicate = user.isOneOf(["CustomerServiceCenter"]) && isPotentialDuplicate
  const [markAsDuplicate] = useMarkPatientAsPotentialDuplicateMutation(country)
  const [rejectDuplicate] = useRejectPatientAsPotentialDuplicateMutation(country)
  const confirm = useConfirmStore((state) => state.confirm)
  const openDrawer = useDrawerStore((state) => state.openDrawer)

  const onHandleMedicalRecord = () => {
    window.open(`/patients/${country}/${patientId}`, patientId)
  }

  const navigate: { (to: To, options?: { state: PatientNameNavigationState }): void } = useNavigate()

  const handleFilterWaitingRoom = () => {
    navigate("/", {
      state: {
        patientName: `${firstName} ${lastName}`,
      },
    })
  }

  const handleRejectDuplicateReport = async () => {
    const confirmed = await confirm({
      title: t("patient.table.rejectDuplicateReport"),
      content: <p>{t("patient.table.rejectDuplicateReportConfirmation")}</p>,
      confirmText: t("patient.table.rejectDuplicateReportButton"),
      cancelText: t("common.button.cancel"),
    })

    if (confirmed) {
      rejectDuplicate({
        variables: { input: { patientId } },
        onCompleted(response) {
          const { boolean } = response.rejectPatientAsPotentialDuplicate
          if (boolean) {
            onPatientUpdated()
            toast({
              description: t("patient.table.rejectDuplicateReportSuccess"),
            })
          }
        },
      })
    }
  }

  const handleMarkAsPotentialDuplicate = () => {
    if (isPotentialDuplicate) {
      toast({
        description: t("patient.table.markAsPotentialDuplicateAlreadyMarked"),
      })
      return
    }

    markAsDuplicate({
      variables: { input: { patientId } },
      onCompleted(response) {
        const { boolean, errors } = response.markPatientAsPotentialDuplicate
        if (boolean) {
          onPatientUpdated()
          toast({
            description: t("patient.table.markAsPotentialDuplicateSuccess"),
          })
        } else if (errors?.some((e) => e.__typename === "PatientAlreadyMarkedAsPotentialDuplicate")) {
          toast({
            description: t("patient.table.markAsPotentialDuplicateAlreadyMarked"),
          })
        }
      },
    })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild className="outline-hidden">
        <Button
          data-testid="action-button"
          variant="circle"
          className="action-button h-9 w-9 p-0 outline-hidden!"
          onClick={(event) => event.stopPropagation()}>
          <DotsThreeVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        hideWhenDetached
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        align="end"
        className="min-w-63 rounded-lg border-none p-2 leading-5 transition-all duration-200">
        <DropdownMenuCheckboxItem
          className="text-on-surface"
          onClick={onHandleMedicalRecord}
          testId="open-medical-record-button">
          {t("patient.table.openMedicalRecord")}
        </DropdownMenuCheckboxItem>
        {!callId && (
          <DropdownMenuCheckboxItem
            testId="document-inbound-call-button"
            className="text-on-surface"
            onClick={() => {
              openPatientInboundCall(patientId, joinStrings([firstName, lastName], " "))
            }}>
            {t("patient.table.documentInboundCall")}
          </DropdownMenuCheckboxItem>
        )}
        <DropdownMenuCheckboxItem
          className="text-on-surface"
          onClick={handleFilterWaitingRoom}
          testId="filter-waiting-room-button">
          {t("patient.table.filterWaitingRoom")}
        </DropdownMenuCheckboxItem>
        {canManageDuplicate ? (
          <>
            <DropdownMenuCheckboxItem
              className="text-on-surface"
              onClick={() => {
                openDrawer(
                  () => (
                    <ManageDuplicatesDrawer
                      country={country}
                      initialPatientId={patientId}
                      onPatientUpdated={onPatientUpdated}
                    />
                  ),
                  "ManageDuplicatesDrawer",
                )
              }}
              testId="manage-duplicate-records-button">
              {t("patient.duplicates.manageDuplicateRecords")}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              className="text-on-surface"
              onClick={handleRejectDuplicateReport}
              testId="reject-duplicate-report-button">
              {t("patient.table.rejectDuplicateReport")}
            </DropdownMenuCheckboxItem>
          </>
        ) : (
          <DropdownMenuCheckboxItem
            className="text-on-surface"
            onClick={handleMarkAsPotentialDuplicate}
            testId="mark-as-potential-duplicate-button">
            {t("patient.table.markAsPotentialDuplicate")}
          </DropdownMenuCheckboxItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { PatientTableActionMenu }
